// Stornobedingungen pflegen (PRD-020 Thema 3, US-301/303/306)
//
// Wortgleiche Übertragung von EventTextListPage.tsx, OHNE Kategorie-Spalte (Entscheidung
// 3.1: flache Liste). Siehe dort für die volle Begründung jeder Zusicherung — hier nur
// die strukturelle Abweichung.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useToast } from '@/hooks/use-toast';
import { useMe } from '@/hooks/usePermissions';
import { ApiError, apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  useCancellationTerms,
  useSaveCancellationTerm,
  useDeleteCancellationTerms,
} from '@/services/event-service';
import { formatDay } from '@/features/events/mailing-match';
import { Role, type CancellationTerm, type SaveCancellationTermRequest } from '@/types';
import { CancellationTermFormDialog } from './CancellationTermFormDialog';
import { pruneSelection, selectionState, toggleAll, toggleOne } from './selection';

/** Textauszug für die Übersichtsspalte — reiner Text (Storno-Folge), nur Kürzung nötig. */
function excerpt(text: string, max = 90): string {
  const plain = text.replace(/\s+/g, ' ').trim();

  return plain.length > max ? `${plain.slice(0, max)}…` : plain;
}

export function CancellationTermListPage() {
  const { t } = useTranslation('settings');
  const { toast } = useToast();

  const { data: me } = useMe();
  /**
   * Darf diese Person die Pflegeseite überhaupt benutzen? Am RECHT, nicht an der Rolle
   * — wortgleich zu canManage in EventTextListPage.
   *
   * ⚠️ Der Server prüft dasselbe Recht (requireCancellationTermsEdit). Das hier blendet
   * nur aus, es sichert nichts ab.
   */
  const canManage = (me?.permissions?.cancellationTerms ?? []).includes('edit');
  /**
   * Gehört diese Bedingung dem Anfragenden — darf er sie also ändern? Spiegelt
   * canEditCancellationTerm im Backend.
   */
  const isCentralRole = me?.role === Role.Zentral || me?.role === Role.Admin;
  const canEdit = (term: CancellationTerm) =>
    isCentralRole ? true : Boolean(term.ownerPartnerKey) && term.ownerPartnerKey === me?.partnerKey;

  const { data: terms = [], isLoading, isError, error } = useCancellationTerms();
  const saveMutation = useSaveCancellationTerm();
  const deleteMutation = useDeleteCancellationTerms();

  const [selected, setSelected] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CancellationTerm | null>(null);
  // Erzwingt einen frischen Mount von CancellationTermFormDialog bei jedem Öffnen
  // (29.09.2026 — wortgleich zu EventTextListPage.tsx's dialogInstance).
  const [dialogInstance, setDialogInstance] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
  const [usage, setUsage] = useState<{ count: number; available: boolean } | null>(null);

  // Nur die änderbaren — wortgleiche Begründung wie in EventTextListPage.
  const ids = useMemo(() => terms.filter(canEdit).map((term) => term.id), [terms]);

  const liveSelection = useMemo(() => pruneSelection(selected, ids), [selected, ids]);

  const headerState = selectionState(terms.length, liveSelection.length);
  const isDenied =
    error instanceof ApiError && (error.status === 401 || error.status === 403);

  // Verweise erst zählen, wenn der Dialog aufgeht — nicht für jede Zeile im Voraus.
  useEffect(() => {
    if (!confirmDelete) {
      setUsage(null);
      return;
    }

    let abgebrochen = false;
    setUsage(null);

    apiClient
      .getCancellationTermUsage(confirmDelete)
      .then((antwort) => {
        if (abgebrochen) return;
        const summe = Object.values(antwort.usage).reduce((a, b) => a + b, 0);
        setUsage({ count: summe, available: antwort.available });
      })
      .catch(() => {
        if (!abgebrochen) setUsage({ count: 0, available: false });
      });

    return () => {
      abgebrochen = true;
    };
  }, [confirmDelete]);

  const openCreate = () => {
    setEditing(null);
    setSaveError(null);
    setDialogInstance((n) => n + 1);
    setDialogOpen(true);
  };

  const openEdit = (term: CancellationTerm) => {
    setEditing(term);
    setSaveError(null);
    setDialogInstance((n) => n + 1);
    setDialogOpen(true);
  };

  const handleSave = (req: SaveCancellationTermRequest) => {
    setSaveError(null);
    saveMutation.mutate(req, {
      onSuccess: () => {
        toast({ title: t('cancellationTerms.toast.saved') });
        setDialogOpen(false);
      },
      onError: (e) => setSaveError(e instanceof Error ? e.message : String(e)),
    });
  };

  const handleDelete = (doomed: string[]) => {
    deleteMutation.mutate(doomed, {
      onSuccess: (result) => {
        setConfirmDelete(null);
        setSelected([]);

        toast({ title: t('cancellationTerms.toast.deleted', { count: result.removed.length }) });

        if (result.failed.length > 0) {
          toast({
            title: t('cancellationTerms.toast.deleteFailed'),
            description: result.failed.map((f) => `${f.id}: ${f.reason}`).join('\n'),
            variant: 'destructive',
          });
        }
      },
      onError: (e) =>
        toast({
          title: t('cancellationTerms.toast.error'),
          description: e instanceof Error ? e.message : String(e),
          variant: 'destructive',
        }),
    });
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">{t('cancellationTerms.page.title')}</h2>
        <p className="p-8 text-center text-muted-foreground">
          {t('cancellationTerms.page.noAccess')}
        </p>
      </div>
    );
  }

  const titleOf = (id: string) => terms.find((term) => term.id === id)?.title ?? id;

  /** Beschreibung im Löschdialog. Kein „Datenverlust"-Vorbehalt nötig (OF-7: kopieren). */
  const deleteDescription = (): string => {
    if (!confirmDelete) return '';

    const kopf =
      confirmDelete.length === 1
        ? t('cancellationTerms.delete.descriptionOne', { title: titleOf(confirmDelete[0]) })
        : t('cancellationTerms.delete.descriptionMany', { count: confirmDelete.length });

    if (!usage) return kopf;
    if (!usage.available) return `${kopf}\n\n${t('cancellationTerms.delete.usageUnavailable')}`;
    if (usage.count === 0) return kopf;

    return `${kopf}\n\n${t('cancellationTerms.delete.usageHint', { count: usage.count })}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <Button asChild variant="ghost" size="sm" className="-ml-2 h-7 gap-1 px-2">
            <Link to="/settings">
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('cancellationTerms.page.backToSettings')}
            </Link>
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">{t('cancellationTerms.page.title')}</h2>
          <p className="text-muted-foreground">{t('cancellationTerms.page.description')}</p>
        </div>
        <Button className="gap-2" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          {t('cancellationTerms.actions.create')}
        </Button>
      </div>

      {isLoading ? (
        <p className="p-8 text-center text-muted-foreground">{t('cancellationTerms.page.loading')}</p>
      ) : isDenied ? (
        <p className="p-8 text-center text-muted-foreground">{t('cancellationTerms.page.noAccess')}</p>
      ) : isError ? (
        <p className="p-8 text-center text-red-600">{t('cancellationTerms.page.loadError')}</p>
      ) : terms.length === 0 ? (
        <p className="rounded-md border p-8 text-center text-muted-foreground">
          {t('cancellationTerms.page.empty')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={headerState === 'all'}
                  onCheckedChange={() => setSelected(toggleAll(ids, liveSelection))}
                  aria-label={t('cancellationTerms.table.selectAll')}
                  className={cn(headerState === 'some' && 'data-[state=checked]:bg-primary/50')}
                />
              </TableHead>
              {/* Keine Kategorie-Spalte — flache Liste (Entscheidung 3.1). */}
              <TableHead>{t('cancellationTerms.table.title')}</TableHead>
              <TableHead>{t('cancellationTerms.table.preview')}</TableHead>
              <TableHead>{t('cancellationTerms.table.modified')}</TableHead>
              <TableHead className="text-right">{t('cancellationTerms.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {terms.map((term) => {
              const isSelected = liveSelection.includes(term.id);
              const mine = canEdit(term);

              return (
                <TableRow key={term.id} className={cn(isSelected && 'bg-blue-50')}>
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      disabled={!mine}
                      onCheckedChange={() => setSelected(toggleOne(liveSelection, term.id))}
                      aria-label={t('cancellationTerms.table.selectRow', { title: term.title })}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{term.title}</TableCell>
                  <TableCell className="max-w-[28rem] text-sm text-muted-foreground">
                    {excerpt(term.text)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDay(term.modifiedAt)}
                    {term.modifiedBy && <div className="truncate">{term.modifiedBy}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!mine}
                      onClick={() => openEdit(term)}
                      aria-label={t('cancellationTerms.actions.edit')}
                      title={mine ? undefined : t('cancellationTerms.actions.foreign')}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!mine}
                      onClick={() => setConfirmDelete([term.id])}
                      aria-label={t('cancellationTerms.actions.delete')}
                      title={mine ? undefined : t('cancellationTerms.actions.foreign')}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {liveSelection.length > 0 && (
        <div className="sticky bottom-0 z-40 flex items-center justify-between gap-4 border-t bg-background/95 p-3 backdrop-blur">
          <span className="text-sm text-muted-foreground">
            {t('cancellationTerms.bulk.selected', { count: liveSelection.length })}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelected([])}>
              {t('cancellationTerms.bulk.clearSelection')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="gap-2"
              onClick={() => setConfirmDelete([...liveSelection])}
            >
              <Trash2 className="h-4 w-4" />
              {t('cancellationTerms.bulk.deleteSelected')}
            </Button>
          </div>
        </div>
      )}

      <CancellationTermFormDialog
        key={dialogInstance}
        open={dialogOpen}
        cancellationTerm={editing}
        saveError={saveError}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        isSaving={saveMutation.isPending}
      />

      <ConfirmationDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t(
          confirmDelete?.length === 1
            ? 'cancellationTerms.delete.title'
            : 'cancellationTerms.delete.titleMany'
        )}
        description={deleteDescription()}
        confirmLabel={t('cancellationTerms.delete.confirm')}
        cancelLabel={t('cancellationTerms.form.cancel')}
        variant="destructive"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
      />
    </div>
  );
}
